/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Utils from "utils/Utils";
import Monster from "model/monster/Monster"
import Attributes from "model/stats/Attributes";
import AttributeValues from "model/stats/AttributeValues";
import SkillSet, { Skill } from "model/stats/SkillSet";
import Section from "model/abilities/Section";
import Abilities from "model/abilities/Abilities";
import { computeSplitPoint } from "model/formats/PageSplitCalculator";
import { handleSpellCasting } from "model/formats/SpellCastingFormatter";
import { italicizeAttackPhrase } from "model/formats/AttackPhraseFormatter";

class Open5e {
    static capitalizeWords = (str: string) => {
        return str && str.replace(/\b\w/g, match => match.toUpperCase());
    }

    static prepAbilityArray = (arry: any[]): Ability[] => {
        const abilities: Ability[] = [];
        // convert any 'description' into 'desc', and make it an Ability object:
        for (const ability of arry || []) {
            abilities.push({
                name:          ability.name,
                desc:          ability.description || ability.desc,
                id:            ability.id,
                label:         ability.label,
                attribute:     ability.attribute,
                group:         ability.group,
                groupOrder:    ability.groupOrder,
                attack_bonus:  ability.attack_bonus,
                damage_dice:   ability.damage_dice,
                damage_bonus:  ability.damage_bonus
            });
        }
        return abilities;
    }

    static translateMonster = (open5eMonster: any): Monster => {
        // add in the default extension fields, in their default state:
        const monster: Monster = new Monster(); //{ ...open5eMonster };

        for (const attr in open5eMonster) {
            if ((typeof open5eMonster[attr] === 'string') && 
                ((open5eMonster[attr].toLowerCase() === 'false') ||
                 (open5eMonster[attr].toLowerCase() === '—'))) {
                delete open5eMonster[attr];
            }
        }

        monster.doc_ref.slug            = open5eMonster.slug;
        monster.desc                    = open5eMonster.desc || open5eMonster.description;
        monster.name                    = open5eMonster.name;
        monster.size                    = Open5e.capitalizeWords(open5eMonster.size);
        monster.type                    = Open5e.capitalizeWords(open5eMonster.type);
        // subrace
        monster.subtype                 = Open5e.capitalizeWords(open5eMonster.subtype);
        monster.group                   = open5eMonster.group;
        monster.alignment               = Open5e.capitalizeWords(open5eMonster.alignment);
        monster.armor_class             = open5eMonster.armor_class;
        monster.armor_desc              = open5eMonster.armor_desc;
        monster.hit_points              = open5eMonster.hit_points;
        monster.hit_dice                = open5eMonster.hit_dice;
        monster.speed                   = open5eMonster.speed;
        monster.attributes.strength     = open5eMonster.strength;
        monster.attributes.dexterity    = open5eMonster.dexterity;
        monster.attributes.constitution = open5eMonster.constitution;
        monster.attributes.intelligence = open5eMonster.intelligence;
        monster.attributes.wisdom       = open5eMonster.wisdom;
        monster.attributes.charisma     = open5eMonster.charisma;
        // open5eMonster.perception is the Perception *modifier*; passive Perception is 10 + that. The
        // senses block below overrides this when the senses string carries an explicit passive value.
        monster.passivePerception       = 10 + (open5eMonster.perception || 0);
        monster.damage_vulnerabilities  = open5eMonster.damage_vulnerabilities;
        monster.damage_resistances      = open5eMonster.damage_resistances;
        monster.damage_immunities       = open5eMonster.damage_immunities;
        monster.condition_immunities    = open5eMonster.condition_immunities;
        monster.senses                  = open5eMonster.senses;
        monster.languages               = open5eMonster.languages;
        monster.challenge_rating        = open5eMonster.challenge_rating;
        monster.cr                      = parseFloat(open5eMonster.cr);
        monster.actions                 = Open5e.prepAbilityArray(open5eMonster.actions);
        monster.bonus_actions           = Open5e.prepAbilityArray(open5eMonster.bonus_actions);
        monster.reactions               = Open5e.prepAbilityArray(open5eMonster.reactions);
        monster.legendary_desc          = open5eMonster.legendary_desc;
        monster.legendary_actions       = Open5e.prepAbilityArray(open5eMonster.legendary_actions);
        monster.mythic_actions          = Open5e.prepAbilityArray(open5eMonster.mythic_actions);
        monster.lair_actions            = Open5e.prepAbilityArray(open5eMonster.lair_actions);
        monster.traits                  = Open5e.prepAbilityArray(open5eMonster.special_abilities || open5eMonster.traits);

        // this block was a bit too agressive in declaring traits as 'bonus Actions' or 'Reactions':
        // // Some source statblocks file "take X as a bonus action" / "use its reaction to X" abilities as
        // // plain traits (e.g. the Alp's "While in dim light or darkness, the [MON] can take the Hide action
        // // as a bonus action"). Move those into the Bonus Actions / Reactions sections, using the same
        // // heuristics the rest of the app uses (Section.isBonusAction / isReaction).
        // const isBonusActionTrait = (t: Ability) =>
        //     typeof t?.desc === 'string' && typeof t?.name === 'string' && Section.isBonusAction(t as any);
        // const isReactionTrait = (t: Ability) =>
        //     typeof t?.desc === 'string' && typeof t?.name === 'string' && Section.isReaction(t as any);
        // const traitBonusActions = monster.traits.filter(isBonusActionTrait);
        // if (traitBonusActions.length) {
        //     monster.traits        = monster.traits.filter(t => !isBonusActionTrait(t));
        //     monster.bonus_actions = [...monster.bonus_actions, ...traitBonusActions];
        // }
        // const traitReactions = monster.traits.filter(isReactionTrait);
        // if (traitReactions.length) {
        //     monster.traits    = monster.traits.filter(t => !isReactionTrait(t));
        //     monster.reactions = [...monster.reactions, ...traitReactions];
        // }

        monster.spell_list              = open5eMonster.spell_list;
        monster.environments            = open5eMonster.environments;
        monster.img_main                = open5eMonster.img_main;
        monster.doc_ref.page_no                 = open5eMonster.page_no;
        monster.doc_ref.document__slug          = open5eMonster.document__slug;
        monster.doc_ref.document__title         = open5eMonster.document__title;
        monster.doc_ref.document__license_url   = open5eMonster.document__license_url;
        monster.doc_ref.document__url           = open5eMonster.document__url;

        monster.proficiencyBonus = Utils.getProfBonusByCR(monster.cr);
        monster.xp = Utils.getXpByCR(monster.cr);

        // Parse the hit dice string using regular expressions
        const hitDiceMatch = monster.hit_dice?.match(/^(\d+)d *(\d+) *([-+] *\d+)?$/);
        if (hitDiceMatch) {
            // Extract the hit dice components
            monster.hitDiceNumber = parseInt(hitDiceMatch[1]) || monster.hitDiceNumber || 1;
            monster.hitDiceType = parseInt(hitDiceMatch[2]) || monster.hitDiceType || 8;
            monster.hitDiceBonus = hitDiceMatch[3] ? parseInt(hitDiceMatch[3].replace(/ /g, '')) : (monster.hitDiceBonus || 0);
            monster.usePcHitDice = open5eMonster.usePcHitDice ?? false;
            monster.useMm2024    = open5eMonster.useMm2024    ?? false;
            monster.hitPointsCustom = `${monster.hit_points} (${monster.hit_dice})`;
        } else {
            if (monster.hit_dice) {
                console.error(`Invalid hit dice format, but still using: ${monster.hit_dice}`);
                monster.hitPointsCustom = monster.hit_dice;
            } else {
                // missing hit dice, using hit_points instead
                monster.hitPointsCustom = '' + monster.hit_points;
            }
        }

        if (monster.senses === '-') {
            monster.senses = '';
        }
        if (monster.senses) {
            const senseMatch = monster.senses?.toLowerCase().match(/.*passive perception (\d+).*/);
            if (senseMatch) {
                monster.passivePerception = parseInt(senseMatch[1]);
            }
        }

        const saving_throws = [];
        for (const attr of Attributes.attributes) {
            const saveKey = attr + '_save';
            if (saveKey in open5eMonster) {
                let attrSave = open5eMonster[saveKey];
                if (attrSave != null) {
                    const attrLevel = open5eMonster[attr];
                    const defaultValue = Attributes.getBonus(attrLevel);
                    if (defaultValue != attrSave) {
                        if (attrSave > 0) {
                            attrSave = "+" + attrSave;
                        }
                        saving_throws.push(attr.substring(0, 3).toUpperCase() + " " + attrSave);
                    }
                }
            }
        }
        monster.saving_throws = saving_throws.join(', ');

        // read in the skills Records
        const skillsObj = open5eMonster.skills instanceof Map
            ? Object.fromEntries(open5eMonster.skills)
            : open5eMonster.skills;
        monster.skillSet = SkillSet.parseFromObject(skillsObj, monster.attributes, monster.proficiencyBonus);
        
        if (monster.desc) {
            if (!monster.traits) {
                monster.traits = [];
            }
            monster.traits.unshift({ name: '', desc: monster.desc });
        }
        // const newSpellList = [];
        // for (const spell of monster.spell_list) {
        //   if (spell.startsWith("https://api.open5e.com/v1/spells")) {
        //     const spellObj = await loadDataFromURL(spell);
        //     newSpellList.push(spellObj);
        //   } else {
        //     newSpellList.push({name:'spell', desc: spell});
        //   }
        // }
        // monster.spell_list = newSpellList;
        this.updateAbilities(monster);

        if (open5eMonster.img_main && open5eMonster.img_main != 'http://api.open5e.com/') {
            //Monster.loadImage(jsonObject.img_main).then((imageData) => monster.image.src = imageData);
            monster.image.src            = open5eMonster.img_main;
            monster.image.width          = 400;
            monster.image.opacity        = 1.0;
            monster.image.positionRight  = -400;
            monster.image.positionBottom = 0;
            monster.image.rotation       = 0;
            monster.image.horizontalFlip = false;
            monster.image.clip           = false;
        }
        if (!monster.gear) {
            const newGear = [];
            const possibleGear = Abilities.getGearNames();
            for (const abilities of [monster.actions, monster.inventory]) {
                for (const ability of abilities || []) {
                    if (possibleGear.includes(ability.name)) {
                        newGear.push(ability.name);
                    }
                }
            }
            monster.gear = newGear.join(', ');
        }

        Open5e.computeSplitPoint(monster);

        return monster;
    }

    static computeSplitPoint = computeSplitPoint;

    static checkForNameInDescs = (mon: any, name: string, arrays: any) => {
        // Use a capturing group to capture the target word
        const pattern = new RegExp(`\\bthe\\s(${name})\\b`, 'i');
        for (const ary of arrays) {
            for (const ability of ary || []) {
                const match = ability.desc.match(pattern);
                if (match) {
                    // match[1] contains the first captured group, which is your target word in its original case
                    return match[1];
                }
            }
        }
    }

    static updateAbilities = (mon: Monster) => {
        const arrays: (Ability | null)[][] = [mon.actions,
                                     mon.bonus_actions,
                                     mon.reactions,
                                     mon.legendary_actions,
                                     mon.traits,
                                     mon.inventory,
                                     mon.mythic_actions,
                                     mon.regional_actions,
                                     mon.lair_actions];
        const strings: {val:string, setter:(newVal:string)=>{}}[] = [
            {val: mon.legendary_desc, setter:(newVal) => mon.legendary_desc = newVal},
            {val: mon.mythic_desc,        setter:(newVal) => mon.mythic_desc       = newVal},
            {val: mon.regional_desc,      setter:(newVal) => mon.regional_desc     = newVal},
            {val: mon.regional_desc_end,  setter:(newVal) => mon.regional_desc_end = newVal},
            {val: mon.lair_desc,          setter:(newVal) => mon.lair_desc         = newVal},
            {val: mon.lair_desc_end,      setter:(newVal) => mon.lair_desc_end     = newVal},
        ];
        if (!mon.shortName) {
            let shortName = Open5e.checkForNameInDescs(mon, mon.name, arrays);
            if (!shortName && (mon.name.includes(' ') || mon.name.includes('\u00A0'))) {
                const monWords = mon.name.split(/\s\(\)/);
                const lastWord = monWords[monWords.length - 1];
                shortName = Open5e.checkForNameInDescs(mon, lastWord, arrays);
            }
            if (shortName) {
                mon.shortName = shortName;
                mon.pluralName = shortName + 's';
            }
        }
        for (const abilityArray of arrays) {
            for (const ability of abilityArray ?? []) {
                if (ability) {
                    this.importAbility(ability, mon);
                }
            }
        }
        for (const str of strings) {
            const obj = { name: '', desc: str.val };
            this.importAbility(obj, mon);
            str.setter(obj.desc);
        }
        mon.spells = mon.traits.filter(ability => ability.name.toLowerCase().includes("spellcasting"));
        mon.traits = mon.traits.filter(ability => !ability.name.toLowerCase().includes("spellcasting"));
    }

    static importAbility = (ability: Ability, mon: Monster) => {
        if (Array.isArray(ability.desc)) {
            ability.desc = ability.desc.join("\n");
        }
        ability.name = ability.name?.trim() ?? '';
        ability.desc = ability.desc?.trim() ?? '';
        const name = mon.shortName || mon.name;
        this.handleName(ability, name);
        this.handleSpellCasting(ability, mon);
        this.italicizeAttackPhrase(ability, mon);
        this.removeUnderscoresIfOneGroupFromAbility(ability);
    }
    static removeUnderscoresIfOneGroupFromAbility(ability: Ability): void {
        ability.desc = ability.desc?.split("\n")
                                    .map(line => Open5e.removeUnderscoresIfOneGroup(line))
                                    .join("\n");
    }
    static removeUnderscoresIfOneGroup(line: string): string {
            // Match all groups of consecutive underscores
        const underscoreGroups = line.match(/( *_ *)+/g);
        
        // Only remove underscores if there is exactly one group of them
        if (underscoreGroups && underscoreGroups.length === 1) {
            return line.replace(/( *_ *)+/g, ' ');
        }
        return line;
    }

    static handleName = (ability: Ability, name: string) => {
        this.replaceString(ability, name + 's', '[MONS]');
        this.replaceString(ability, name, '[MON]');
    }
    static replaceString = (ability: Ability, name: string, replacement: string) => {
        // Escape special characters in the monster's name to use in a regular expression
        const escapedMonName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Create a regular expression that matches the monster's name as a whole word
        const regex = new RegExp(`\\b${escapedMonName}\\b`, 'gi');
        // Replace all matches in the description with [MON]
        ability.desc = ability.desc.replace(regex, replacement);
    }

    static handleSpellCasting = handleSpellCasting;

    static italicizeAttackPhrase = italicizeAttackPhrase;

}
export default Open5e;
